import { useUploadVersion } from '@/app/(dashboard)/upload-version/UploadVersionProvider'
import { Button, Icon, IconColor, useFSTrackEvent } from '@/dc-shared-web/src'
import { faArrowRightArrowLeft } from '@document-crunch/pro-regular-svg-icons'
import { FC } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { step1Schema } from '@/app/(dashboard)/upload-version/step1/Step1Schema'
import { PickFile } from '@/app/(dashboard)/components/PickFile'
import { FileItem } from '@/app/(dashboard)/components/FileItem'
import { FS, FS_CATEGORY } from '@/dc-shared-web/src/lib/trackingConstants'

export type Step1Props = {
  onCancel: () => void
}

export const Step1: FC<Step1Props> = ({ onCancel }) => {
  const { nextStep, updateStepsData, stepsData, document } = useUploadVersion()
  const { trackEvent } = useFSTrackEvent()

  const form = useForm<z.infer<typeof step1Schema>>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      file: stepsData.step1.file,
    },
  })

  const onFileSelected = (file: File) => {
    form.setValue('file', file)
    form.trigger('file')
  }

  const onSubmit = () => {
    if (!form.formState.isValid) return

    trackEvent(FS.BUTTON_CLICKED, {
      name: 'Step 1',
      page_category: FS_CATEGORY.VERSION_COMPARISON,
      entity_id: document.uuid,
      entity_type: 'crunch',
    })

    const data = form.getValues()

    updateStepsData('step1', {
      file: data.file,
    })

    nextStep()
  }

  const file = useWatch({
    control: form.control,
    name: 'file',
  })

  const onFileRemove = () => {
    form.setValue('file', undefined)
    form.trigger('file')
  }

  const fileError = form.formState.errors.file?.message
  const isFormValid = form.formState.isValid

  return (
    <div className='tw-space-y-4'>
      <p>Comparing 2 versions of the same document:</p>
      <div className='tw-space-y-4'>
        <div className='tw-space-y-2'>
          <p className='tw-font-medium tw-text-sm'>Original document</p>
          <p className='tw-font-medium'>{document.fileName}</p>
        </div>
        <div className='tw-space-y-2'>
          <p className='tw-font-medium tw-text-sm'>New version</p>
          {file ? (
            <FileItem
              file={file}
              onRemove={onFileRemove}
              errorMessage={fileError}
            />
          ) : (
            <PickFile onFileSelected={onFileSelected} />
          )}
        </div>
      </div>
      <div className='tw-flex tw-items-center tw-justify-end tw-gap-2'>
        <Button
          onClick={() => {
            trackEvent(FS.BUTTON_CLICKED, {
              name: 'Cancel',
              page_category: FS_CATEGORY.VERSION_COMPARISON,
              entity_id: document.uuid,
              entity_type: 'crunch',
            })
            onCancel()
          }}
          variant='outline'
          data-testid='cancel-button'
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            form.handleSubmit(onSubmit)()
          }}
          type='button'
          data-testid='compare-versions-btn'
          disabled={!isFormValid}
        >
          <div className='tw-flex tw-items-center tw-gap-2'>
            <Icon
              icon={faArrowRightArrowLeft}
              size='1x'
              color={IconColor.white}
            />
            <span>Compare versions</span>
          </div>
        </Button>
      </div>
    </div>
  )
}
